import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Ban,
  BatteryLow,
  Building2,
  Check,
  CircleFadingArrowUp,
  CircleHelp,
  CreditCard,
  DoorOpen,
  Radio,
  TriangleAlert,
  WifiOff,
  X,
  type LucideIcon,
} from "lucide-react";
import { HoverPopover } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  getDeviceStatusData,
  getInUseCheckStatuses,
  getInUseWarningKinds,
  getKioskMinAppVersion,
  getSetupBadgeKind,
  getTelemetryStatus,
  IN_USE_WARNING_KIND_ORDER,
  readTelemetryNumber,
  telemetryStatusDotClass,
  type InUseCheckStatus,
  type InUseWarningKind,
  type TelemetryStatus,
} from "@/lib/deviceStatus";
import type { Device } from "@/types";

type DeviceRow = Pick<
  Device,
  | "software_id"
  | "last_login_at"
  | "last_device_report_at"
  | "device_status_payload"
  | "device_location_payload"
  | "house_id"
  | "is_revoked"
> & {
  reader?: Device["reader"];
};

type InUseWarningDevice = Pick<
  Device,
  | "is_shelved"
  | "last_device_report_at"
  | "house_id"
  | "room_name"
  | "device_status_payload"
  | "device_location_payload"
> & {
  reader?: Device["reader"];
};

const IN_USE_WARNING_DETAIL_KEYS: Record<InUseWarningKind, string> = {
  staleTelemetry: "page.inUseWarningStaleTelemetry",
  outdatedAppVersion: "page.inUseWarningOutdatedAppVersion",
  deviceLowBattery: "page.inUseWarningDeviceLowBattery",
  readerLowBattery: "page.inUseWarningReaderLowBattery",
  missingReader: "page.inUseWarningMissingReader",
  missingStructure: "page.inUseWarningMissingStructure",
  missingRoom: "page.inUseWarningMissingRoom",
};

const IN_USE_CHECK_LABEL_KEYS: Record<InUseWarningKind, string> = {
  staleTelemetry: "page.inUseCheckTelemetry",
  outdatedAppVersion: "page.inUseCheckAppVersion",
  deviceLowBattery: "page.inUseCheckDeviceBattery",
  readerLowBattery: "page.inUseCheckReaderBattery",
  missingReader: "page.inUseCheckReader",
  missingStructure: "page.inUseCheckStructure",
  missingRoom: "page.inUseCheckRoom",
};

const IN_USE_CHECK_ICONS: Record<InUseWarningKind, LucideIcon> = {
  staleTelemetry: Radio,
  outdatedAppVersion: CircleFadingArrowUp,
  deviceLowBattery: BatteryLow,
  readerLowBattery: BatteryLow,
  missingReader: CreditCard,
  missingStructure: Building2,
  missingRoom: DoorOpen,
};

const TELEMETRY_TOOLTIP_KEYS: Record<TelemetryStatus, string> = {
  gray: "page.telemetryTooltipGray",
  green: "page.telemetryTooltipGreen",
  yellow: "page.telemetryTooltipYellow",
  red: "page.telemetryTooltipRed",
};

/** Battery percent at or below this shows a low-battery alert. */
const LOW_BATTERY_LEVEL = 25;
/** Wi-Fi signal at or below this shows a weak/offline Wi-Fi alert. */
const WEAK_WIFI_LEVEL = 10;

/** Shared hit target for serial-row status icons (matches the revoked badge). */
const STATUS_ICON_HIT =
  "inline-flex shrink-0 cursor-default items-center justify-center rounded-md p-1 transition";

type StatusIconProps = {
  label: string;
  className: string;
  placement?: "above" | "below";
  content?: ReactNode;
  contentClassName?: string;
  children: ReactNode;
};

function StatusIcon({
  label,
  className,
  placement = "above",
  content,
  contentClassName,
  children,
}: StatusIconProps) {
  return (
    <HoverPopover
      content={content ?? label}
      placement={placement}
      contentClassName={contentClassName}
    >
      <span className={cn(STATUS_ICON_HIT, className)} aria-label={label}>
        {children}
      </span>
    </HoverPopover>
  );
}

/** Colored telemetry status for every device. */
export function DeviceTelemetryDot({ device }: { device: DeviceRow }) {
  const { t } = useTranslation("devices");
  const status = getTelemetryStatus(device);
  const label = t(TELEMETRY_TOOLTIP_KEYS[status]);

  return (
    <HoverPopover content={label} placement="above">
      <span
        className="-m-1 inline-flex shrink-0 cursor-default items-center justify-center rounded-full p-1"
        aria-label={label}
      >
        <span
          className={cn(
            "h-2.5 w-2.5 rounded-full",
            telemetryStatusDotClass(status),
          )}
          aria-hidden
        />
      </span>
    </HoverPopover>
  );
}

/** Problem-only icons (low battery, weak Wi-Fi), shown beside the in-use warning. */
export function DeviceAlertIcons({ device }: { device: DeviceRow }) {
  const { t } = useTranslation("devices");
  const statusData = getDeviceStatusData(device.device_status_payload);
  const batteryLevel = readTelemetryNumber(statusData, "battery", "level");
  const wifiSignal = readTelemetryNumber(statusData, "wifi", "signalLevel");

  const showLowBattery =
    batteryLevel !== null && batteryLevel <= LOW_BATTERY_LEVEL;
  const showWeakWifi = wifiSignal !== null && wifiSignal <= WEAK_WIFI_LEVEL;

  if (!showLowBattery && !showWeakWifi) {
    return null;
  }

  return (
    <>
      {showLowBattery ? (
        <StatusIcon
          label={t("page.alertLowBattery", { level: Math.round(batteryLevel) })}
          className="text-error-600 hover:bg-error-50"
        >
          <BatteryLow className="h-4 w-4" aria-hidden />
        </StatusIcon>
      ) : null}
      {showWeakWifi ? (
        <StatusIcon
          label={t("page.alertWifiWeak")}
          className="text-error-600 hover:bg-error-50"
        >
          <WifiOff className="h-4 w-4" aria-hidden />
        </StatusIcon>
      ) : null}
    </>
  );
}

/** Revoked-only badge (incomplete setup no longer shows an icon). */
export function DeviceSetupBadge({ device }: { device: DeviceRow }) {
  const { t } = useTranslation("devices");
  if (getSetupBadgeKind(device) !== "revoked") {
    return null;
  }

  const label = t("page.setupBadgeRevoked");
  return (
    <StatusIcon
      label={label}
      content={t("page.setupBadgeRevokedTooltip")}
      placement="below"
      className="text-error-600 hover:bg-error-50"
    >
      <Ban className="h-4 w-4" aria-hidden />
    </StatusIcon>
  );
}

/**
 * Yellow warning beside the serial (tablet name) for devices that are in use but
 * missing associations, stale on telemetry (> 24 h), or with a low tablet/reader battery.
 */
const IN_USE_CHECK_STATUS_STYLES: Record<InUseCheckStatus, string> = {
  ok: "bg-success-50/40 text-success-700/55",
  issue: "bg-warning-50 text-warning-800",
  unknown: "bg-surface-100 text-surface-500",
};

function InUseCheckStatusIcon({ status }: { status: InUseCheckStatus }) {
  const Icon =
    status === "ok" ? Check : status === "unknown" ? CircleHelp : X;
  return <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />;
}

export function DeviceInUseWarning({ device }: { device: InUseWarningDevice }) {
  const { t } = useTranslation("devices");
  const kinds = getInUseWarningKinds(device);
  if (kinds.length === 0) {
    return null;
  }

  const outdatedDetail = t("page.inUseWarningOutdatedAppVersion", {
    version: getKioskMinAppVersion() ?? "",
  });
  const detailFor = (kind: InUseWarningKind) =>
    kind === "outdatedAppVersion"
      ? outdatedDetail
      : t(IN_USE_WARNING_DETAIL_KEYS[kind]);

  const title = t("page.inUseWarningTitle");
  const statuses = getInUseCheckStatuses(device);
  const reasons = kinds.map((kind) => detailFor(kind));

  return (
    <StatusIcon
      label={`${title}: ${reasons.join("; ")}`}
      placement="below"
      className="text-warning-600 hover:bg-warning-50"
      contentClassName="w-72 p-2.5 font-normal"
      content={
        <div className="space-y-2">
          <p className="text-sm font-semibold text-surface-900">{title}</p>
          <div className="grid grid-cols-2 gap-1.5">
            {IN_USE_WARNING_KIND_ORDER.map((kind) => {
              const status = statuses[kind];
              const Icon = IN_USE_CHECK_ICONS[kind];
              const label = t(IN_USE_CHECK_LABEL_KEYS[kind]);
              const detail =
                status === "ok"
                  ? t("page.inUseCheckOk")
                  : status === "unknown"
                    ? t("page.inUseCheckUnknown")
                    : detailFor(kind);
              return (
                <div
                  key={kind}
                  title={detail}
                  className={cn(
                    "flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium",
                    IN_USE_CHECK_STATUS_STYLES[status],
                  )}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  <InUseCheckStatusIcon status={status} />
                </div>
              );
            })}
          </div>
        </div>
      }
    >
      <TriangleAlert className="h-4 w-4" aria-hidden />
    </StatusIcon>
  );
}
